import React, { useState, useEffect } from 'react';
import {
  setErrorReportingContextEntry,
  removeErrorReportingContextEntry,
  ErrorReportingContextEntry,
} from 'app/utils/errorReportingContext';
import { Caller, ITimeoutWarning } from 'app/services/rtc/constants';
import ConnectionState from 'app/services/rtc/ConnectionState';
import SubscribableState from 'app/utils/SubscribableState';
import createStateContext from 'app/utils/createStateContext';

export interface IRtcState {
  readonly caller?: Caller;
  readonly signalingState: ConnectionState;
  readonly signalingError?: Error;
  readonly signalingTimeout?: ITimeoutWarning;
}

const getDefaultRtcState = (): IRtcState => ({
  // Server-assigned identity
  caller:           undefined,

  // State of the signaling server connection
  signalingState:   ConnectionState.Off,
  signalingError:   undefined,
  signalingTimeout: undefined,
});

const updateErrorReportingRtcEntry = (state: IRtcState) => {
  setErrorReportingContextEntry(
    ErrorReportingContextEntry.rtcState,
    state,
  );
};

const clearErrorReportingRtcEntry = () => {
  removeErrorReportingContextEntry(
    ErrorReportingContextEntry.rtcState,
  );
};

const {
  Provider,
  useStore,
} = createStateContext<IRtcState>();
export const useRtcStateStore = useStore;

export type RtcStateStore = SubscribableState<IRtcState>;

export const RtcStateProvider: React.FunctionComponent<{ init?: IRtcState }> = ({
  init,
  children,
}) => {
  const [store] = useState(() => new SubscribableState(init ?? getDefaultRtcState()));

  useEffect(() => {
    updateErrorReportingRtcEntry(store.state);

    const unsubscribe = store.subscribe(() => {
      updateErrorReportingRtcEntry(store.state);
    });

    return () => {
      unsubscribe();
      clearErrorReportingRtcEntry();
    };
  }, [store]);

  return (
    <Provider store={store}>
      {children}
    </Provider>
  );
};

export const getRtcState = (store: RtcStateStore | undefined): IRtcState => (
  store?.state ?? getDefaultRtcState()
);

export const getIsJoined = (store: RtcStateStore | undefined) => {
  const { signalingState } = getRtcState(store);

  return signalingState === ConnectionState.On;
};

export const useRtcState = () => getRtcState(useRtcStateStore());

/*
 * Warning! These are only supposed to be used in RTCProvider
 * or tests. Use them only if you know what you are doing
 */
// Actions
export const setCaller = (store: RtcStateStore, caller: Caller) => {
  store.setState({ caller });
};
export const setSignalingState = (store: RtcStateStore, signalingState: ConnectionState) => {
  store.setState({ signalingState });
};
export const setSignalingError = (store: RtcStateStore, signalingError: Error | undefined) => {
  store.setState({ signalingError });
};
export const setSignalingTimeout = (store: RtcStateStore, signalingTimeout: ITimeoutWarning | undefined) => {
  store.setState({ signalingTimeout });
};
